import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { ExhibitDetail } from '../firestore-models/exhibit-detail';
import { ExhibitDetailAttribute } from '../firestore-models/exhibit-detail-attribute';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiExhibitDetail } from '../api-client-module';
import { ApiSetExhibitDetailsRequest } from '../api-client-module'

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { ExhibitDetailDialogComponent } from './exhibit-detail-dialog/exhibit-detail-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-exhibit-detail',
  templateUrl: './exhibit-detail.component.html',
  styleUrls: ['./exhibit-detail.component.css'],
  standalone: false,
})
export class ExhibitDetailComponent implements OnInit, OnDestroy {

  //Exhibitコレクション
  private exhibitDetailsCollection!: AngularFirestoreCollection<ExhibitDetail>;
  exhibitDetailsObservable!: Observable<ExhibitDetail[]>;
  exhibitDetailsSubscribe!: Subscription;
  private exhibitDetailAttributesCollection!: AngularFirestoreCollection<ExhibitDetailAttribute>;
  exhibitDetailAttributesObservable!: Observable<ExhibitDetailAttribute[]>;
  exhibitDetailAttributesSubscribe!: Subscription;
  //各展示情報に紐付くプロジェクトID
  projectId: string;
  //各展示情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //展示情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select',  'exhibit_detail_id', 'attribute', 'name', 'file', 'date'];
  dataSource = new MatTableDataSource<ExhibitDetail>();
  selection = new SelectionModel<ExhibitDetail>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'exhibitDetailAttributes';
  exhibitDetailAttributes!: Array<ExhibitDetailAttribute>;

  searchFilter: any = {};

  attrCollectionName = AppConst.FIRESTORE_EXHIBIT_DETAIL_ATTR;
  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  // ユーザータイプ
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;
  userType!: number;
  isEdit: boolean = false;

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  currentLang: string = 'ja';
  projectBaseLang: string = 'ja';

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.userType = this.configurationService.getUserType();
    this.isEdit = this.configurationService.getIsEdit();
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    // 展示物取得
    this.exhibitDetailsCollection = this.angularFirestore.collection<ExhibitDetail>(
      AppConst.FIRESTORE_EXHIBIT_DETAILS, ref => ref.where('project_id', '==', this.projectId).orderBy('exhibit_detail_id', 'asc'));
    this.exhibitDetailsObservable = this.exhibitDetailsCollection.valueChanges({ idField: 'id' });
    this.exhibitDetailsSubscribe = this.exhibitDetailsObservable.subscribe(exhibitDetails => {
      this.dataSource.data = this.addFilterValue(exhibitDetails);
    });
    // 展示属性取得
    this.exhibitDetailAttributesCollection = this.angularFirestore.collection<ExhibitDetailAttribute>(
      AppConst.FIRESTORE_EXHIBIT_DETAIL_ATTR, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.exhibitDetailAttributesObservable = this.exhibitDetailAttributesCollection.valueChanges({ idField: 'id' });
    this.exhibitDetailAttributesSubscribe = this.exhibitDetailAttributesObservable.subscribe(exhibitDetailAttributes => {
      this.exhibitDetailAttributes = exhibitDetailAttributes;
    });
    this.currentLang = this.translate.currentLang;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.exhibitDetailsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ExhibitDetail): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(exhibitDetail: ExhibitDetail): void {
    let apiExhibitDetail: ApiExhibitDetail = {
      id: exhibitDetail.id,
      exhibit_detail_id: exhibitDetail.exhibit_detail_id,
      exhibit_detail_attribute_id: exhibitDetail.exhibit_detail_attribute_id,
      exhibit_detail_names: exhibitDetail.exhibit_detail_names,
      exhibit_detail_descs: exhibitDetail.exhibit_detail_descs,
      exhibit_detail_files: exhibitDetail.exhibit_detail_files,
      youtube_codes: exhibitDetail.youtube_codes,
      use_youtube: exhibitDetail.use_youtube,
      externallink_names: exhibitDetail.externallink_names,
      externallink_urls: exhibitDetail.externallink_urls,
      use_externallink: exhibitDetail.use_externallink,
      not_overlap_guide: exhibitDetail.not_overlap_guide,
      project_id: exhibitDetail.project_id,
      enabled: exhibitDetail.enabled,
    };
    const dialogRef = this.dialog.open(ExhibitDetailDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, exhibitDetail: apiExhibitDetail }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ExhibitDetailDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, exhibitDetail: null }
    });
  }

  //削除
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedExhibitDetails: Array<ApiExhibitDetail> = new Array<ApiExhibitDetail>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedExhibitDetail: ApiExhibitDetail = {
            id: row.id,
            exhibit_detail_id: row.exhibit_detail_id,
            exhibit_detail_attribute_id: row.exhibit_detail_attribute_id,
            exhibit_detail_names: row.exhibit_detail_names,
            exhibit_detail_descs: row.exhibit_detail_descs,
            exhibit_detail_files: row.exhibit_detail_files,
            youtube_codes: row.youtube_codes,
            use_youtube: row.use_youtube,
            externallink_names: row.externallink_names,
            externallink_urls: row.externallink_urls,
            use_externallink: row.use_externallink,
            not_overlap_guide: row.not_overlap_guide,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedExhibitDetails.push(selectedExhibitDetail);
        }
      }
      if (!!result) {
        let request: ApiSetExhibitDetailsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          exhibit_details: selectedExhibitDetails
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setExhibitDetails(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              if (response!.ret === AppConst.API_RET_IN_USE_DATA) {
                this.toastr.error(this.translate.instant('削除できませんでした。フロア・表示グループ情報で利用中のデータが含まれている可能性があります。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'exhibit_detail_id':
          return compare(a.exhibit_detail_id, b.exhibit_detail_id, isAsc);
        case 'attribute':
          const aAttr = a.exhibit_detail_attribute_id ? a.exhibit_detail_attribute_id : '';
          const bAttr = b.exhibit_detail_attribute_id ? b.exhibit_detail_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.exhibit_detail_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.exhibit_detail_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.exhibit_detail_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.exhibit_detail_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
